import React, { useState, useEffect } from 'react';
import { Globe, Check, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
  { code: 'fr', label: 'Français', flag: 'FR' },
];

const TranslationEditor = ({ storyId, originalStory }) => {
  const { token } = useAuth();
  const [selectedLang, setSelectedLang] = useState('en');
  const [translation, setTranslation] = useState({ title: '', description: '', pages: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableLangs, setAvailableLangs] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (storyId) {
      loadAvailableTranslations();
    }
  }, [storyId]);

  useEffect(() => {
    if (storyId && selectedLang) {
      loadTranslation(selectedLang);
    }
  }, [storyId, selectedLang]);

  const loadAvailableTranslations = async () => {
    try {
      const res = await axios.get(`${API}/stories/${storyId}/translations`);
      setAvailableLangs(res.data.available_languages || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadTranslation = async (lang) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stories/${storyId}/translations/${lang}`);
      if (res.data.translation) {
        setTranslation(res.data.translation);
      } else {
        // Initialize empty translation from original
        setTranslation({
          title: '',
          description: '',
          pages: originalStory.pages.map(p => ({ page_number: p.page_number, text: '' })),
        });
      }
    } catch (e) {
      setTranslation({
        title: '',
        description: '',
        pages: originalStory.pages.map(p => ({ page_number: p.page_number, text: '' })),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!translation.title.trim()) {
      toast.error('Vul de vertaalde titel in');
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/stories/${storyId}/translations/${selectedLang}`, translation, { headers });
      toast.success(`Vertaling (${selectedLang.toUpperCase()}) opgeslagen!`);
      loadAvailableTranslations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const updatePageText = (pageNum, text) => {
    setTranslation(prev => ({
      ...prev,
      pages: prev.pages.map(p => p.page_number === pageNum ? { ...p, text } : p),
    }));
  };

  if (!storyId) return null;

  return (
    <Card className="border-2 border-blue-300/50 shadow-lg" data-testid="translation-editor">
      <CardHeader
        className="bg-gradient-to-r from-blue-50/50 to-gold/5 dark:from-blue-950/20 dark:to-gold/5 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="font-display text-2xl flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            Vertalingen
            {availableLangs.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({availableLangs.map(l => l.toUpperCase()).join(', ')} beschikbaar)
              </span>
            )}
          </CardTitle>
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="p-4 md:p-6 space-y-6">
          {/* Language selector */}
          <div className="flex gap-2">
            {LANGUAGES.map(l => (
              <Button
                key={l.code}
                type="button"
                variant={selectedLang === l.code ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedLang(l.code)}
                className="rounded-full"
                data-testid={`translation-lang-${l.code}`}
              >
                <span className="font-bold mr-1">{l.flag}</span>
                {l.label}
                {availableLangs.includes(l.code) && <Check className="w-3 h-3 ml-1 text-green-500" />}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Title & Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Origineel (NL)</Label>
                  <div className="p-3 bg-muted/30 rounded-lg text-sm">{originalStory.title}</div>
                </div>
                <div className="space-y-2">
                  <Label className="font-accent">Titel ({selectedLang.toUpperCase()})</Label>
                  <Input
                    data-testid="translation-title"
                    value={translation.title}
                    onChange={(e) => setTranslation({ ...translation, title: e.target.value })}
                    placeholder={`Vertaalde titel (${selectedLang.toUpperCase()})...`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Origineel (NL)</Label>
                  <div className="p-3 bg-muted/30 rounded-lg text-sm">{originalStory.description}</div>
                </div>
                <div className="space-y-2">
                  <Label className="font-accent">Beschrijving ({selectedLang.toUpperCase()})</Label>
                  <Textarea
                    data-testid="translation-description"
                    value={translation.description}
                    onChange={(e) => setTranslation({ ...translation, description: e.target.value })}
                    placeholder={`Vertaalde beschrijving...`}
                    rows={3}
                  />
                </div>
              </div>

              {/* Pages */}
              <div className="space-y-4">
                <h3 className="font-display text-lg">Pagina's</h3>
                {translation.pages.map((page, idx) => {
                  const originalPage = originalStory.pages.find(p => p.page_number === page.page_number);
                  return (
                    <div key={page.page_number} className="border border-border rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gold/20 flex items-center justify-center text-sm font-bold text-gold">
                          {page.page_number}
                        </div>
                        <span className="text-sm font-medium">Pagina {page.page_number}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Origineel (NL)</Label>
                          <div className="p-3 bg-muted/30 rounded-lg text-sm leading-relaxed max-h-32 overflow-y-auto">
                            {originalPage?.text || ''}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Vertaling ({selectedLang.toUpperCase()})</Label>
                          <Textarea
                            data-testid={`translation-page-${page.page_number}`}
                            value={page.text}
                            onChange={(e) => updatePageText(page.page_number, e.target.value)}
                            placeholder={`Vertaling pagina ${page.page_number}...`}
                            rows={4}
                            className="resize-none text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Save button */}
              <Button
                type="button"
                data-testid="save-translation"
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-5"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Vertaling ({selectedLang.toUpperCase()}) opslaan
              </Button>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default TranslationEditor;
